import { z } from "zod";
import { createFileRoute } from "@tanstack/react-router";
import { TablePagination, useTheme, useMediaQuery, Snackbar, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useState } from "react";

import ListLayout from "../../../../components/Layout/ListLayout";
import { useAuth } from "../../../../auth";
import { enableProcessDefinitionById, disableProcessDefinitionById } from '../../../../api/bpm/endpoints';
import { listProcessdefinitionFlat } from '../../../../utils/bpm/apiHelpers';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ProcessDefinitionList from "../../../../components/ProcessDefinitions/ProcessDefinitionList";
import ProcessDefinitionCards from "../../../../components/ProcessDefinitions/ProcessDefinitionCards";
import ProcessDefinitionFilters, { ProcessDefinitionFilterValues } from "../../../../components/ProcessDefinitions/ProcessDefinitionFilters";
import { encodeNavParams } from "../../../../utils/navParams";

const startProcessSearchSchema = z.object({
    page: z.number().catch(1),
    size: z.number().catch(10),
    processDefinitionId: z.string().optional(),
    situation: z.enum(["", "ATIVO", "INATIVO"]).optional(),
    category: z.string().optional(),
    deployTimeFrom: z.string().optional(),
    deployTimeTo: z.string().optional(),
    orderBy: z.string().catch('name'),
    order: z.enum(['asc', 'desc']).catch('asc'),
});

export const Route = createFileRoute('/__auth/processes/start/')({
    component: StartProcessPage,
    validateSearch: (search) => startProcessSearchSchema.parse(search),
    staticData: {
        title: "routes.startProcess",
    },
});

function StartProcessPage() {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { user, currentTenant } = useAuth();
    const routeNavigate = Route.useNavigate();
    const search = Route.useSearch();
    const queryClient = useQueryClient();
    
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
        open: false,
        message: "",
        severity: "success"
    });

    const queryParams = {
        page: (search.page || 1) - 1,
        size: search.size || 10,
        sort: [`${search.orderBy || 'name'},${search.order || 'asc'}`],
        byId: search.processDefinitionId || undefined,
        situations: (search.situation || undefined) as 'ATIVO' | 'INATIVO' | undefined,
        categories: search.category ? [search.category] : undefined,
        startDate: search.deployTimeFrom ? `${search.deployTimeFrom}T00:00:00Z` : undefined,
        endDate: search.deployTimeTo ? `${search.deployTimeTo}T23:59:59Z` : undefined,
        latestVersion: true,
    };

    const { data: dataRaw, isLoading, error } = useQuery({
        queryKey: ['processDefinitions', queryParams, currentTenant],
        queryFn: () => listProcessdefinitionFlat(currentTenant || '', {
            pageable: {
                page: queryParams.page,
                size: queryParams.size,
                sort: queryParams.sort,
            },
            filtro: {
                byId: queryParams.byId,
                situations: queryParams.situations ? [queryParams.situations] : undefined,
                categories: queryParams.categories,
                startDate: queryParams.startDate,
                endDate: queryParams.endDate,
                latestVersion: queryParams.latestVersion
            }
        }),
        enabled: !!user?.id && !!currentTenant,
    });
    const data = (dataRaw as any)?.data || dataRaw;

    const handlePageChange = (_: unknown, value: number) => {
        routeNavigate({
            search: { ...search, page: value },
            replace: true
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleFilter = (newFilters: ProcessDefinitionFilterValues) => {
        routeNavigate({
            search: {
                ...search,
                ...newFilters,
                page: 1
            },
        });
    };

    const handleRequestSort = (property: string) => {
        const isAsc = search.orderBy === property && search.order === 'asc';
        routeNavigate({
            search: {
                ...search,
                orderBy: property,
                order: isAsc ? 'desc' : 'asc'
            }
        });
    };

    const handleStartProcess = (processDefinition: any) => {
        const fromParams = encodeNavParams({ page: search.page, size: search.size, orderBy: search.orderBy, order: search.order });
        routeNavigate({
            to: '/processes/start/$processDefinitionId/execute',
            params: { processDefinitionId: processDefinition.id },
            search: (() => ({ from: 'start' as const, fromParams })) as any
        });
    };

    const handleHistory = (processDefinition: any) => {
        routeNavigate({
            to: '/admin/audit-logs' as any,
            search: { resourceType: 'PROCESS_DEFINITION', resourceId: processDefinition.key } as any
        });
    };

    const toggleActiveMutation = useMutation({
        mutationFn: async (processDefinition: any) => {
            if (!currentTenant) return;
            if (processDefinition.suspended) {
                return enableProcessDefinitionById(currentTenant, processDefinition.id, {
                    activateProcessInstances: true
                });
            } else {
                return disableProcessDefinitionById(currentTenant, processDefinition.id, {
                    suspendProcessInstances: true
                });
            }
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['processDefinitions'] });
            setSnackbar({
                open: true,
                severity: 'success',
                message: variables.suspended ? t('common.activatedSuccess', 'Ativado com sucesso') : t('common.inactivatedSuccess', 'Inativado com sucesso')
            });
        },
        onError: () => {
            setSnackbar({
                open: true,
                severity: 'error',
                message: t('common.errorOccurred', 'Ocorreu um erro')
            });
        }
    });

    const handleToggleActive = (processDefinition: any) => {
        toggleActiveMutation.mutate(processDefinition);
    };

    return (
        <ListLayout
            title={t("processes.startProcess")}
            subtitle={t("processes.startSubtitle")}
            filters={
                <ProcessDefinitionFilters
                    onFilter={handleFilter}
                    isLoading={isLoading}
                    initialValues={{
                        processDefinitionId: search.processDefinitionId,
                        situation: search.situation,
                        category: search.category,
                        deployTimeFrom: search.deployTimeFrom,
                        deployTimeTo: search.deployTimeTo,
                    }}
                />
            }
            error={error}
            isLoading={isLoading}
            isEmpty={!data?.content?.length}
            emptyMessage={t("processes.startEmptyMessage")}
        >
            {isMobile ? (
                <ProcessDefinitionCards
                    processDefinitions={data?.content || []}
                    onStartProcess={handleStartProcess}
                    onToggleActive={handleToggleActive}
                    onHistory={handleHistory}
                />
            ) : (
                <ProcessDefinitionList
                    processDefinitions={data?.content || []}
                    onStartProcess={handleStartProcess}
                    onToggleActive={handleToggleActive}
                    onHistory={handleHistory}
                    orderBy={search.orderBy || 'name'}
                    order={search.order || 'asc'}
                    onRequestSort={handleRequestSort}
                />
            )}

            <TablePagination
                component="div"
                count={data?.totalElements || 0}
                page={(search.page || 1) - 1}
                onPageChange={(_, newPage) => handlePageChange(null, newPage + 1)}
                rowsPerPage={search.size || 10}
                onRowsPerPageChange={(e) => {
                    const newSize = parseInt(e.target.value, 10);
                    routeNavigate({
                        search: { ...search, size: newSize, page: 1 },
                        replace: true
                    });
                }}
                labelRowsPerPage={t("common.rowsPerPage")}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                    mt: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    mb: 8
                }}
            />
            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
                    severity={snackbar.severity} 
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </ListLayout>
    );
}
